import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import {
  getMessageThread,
  replyMessageThread,
  openMessageThread,
  sendWorkshopBookingMessage,
  resolveShopIdFromUrl,
  MAX_MESSAGE_LENGTH,
} from '../../services/messageThreads';
import { horaireLong, majuscule } from '../../annuaire/tunnel/creneaux';
import {
  messageThreadStatusKey,
  messageThreadStatusColor,
  messageThreadActionErrorKey,
} from '../../utils/messageThreadStatus';
import AccountNav from '../../components/customerArea/shared/AccountNav';
import Row from '../../components/shared/atoms/Row';
import Box from '../../components/shared/atoms/Box';
import Container from '../../components/shared/atoms/Container';
import Heading from '../../components/shared/atoms/Heading';
import Text from '../../components/shared/atoms/Text';
import Link from '../../components/shared/atoms/Link';
import Badge from '../../components/shared/atoms/Badge';
import Button from '../../components/shared/atoms/Button';
import Input from '../../components/shared/atoms/Input';

const ErrorBox = styled.div`
  padding: 14px;
  margin: 16px 0;
  background: ${(p) => p.theme.colors.red.pale};
  color: ${(p) => p.theme.colors.red.dark};
  border-radius: 8px;
  font-size: 14px;
`;

const StyledBox = styled(Box)`
  border: 1px solid ${(p) => p.theme.colors.gray.light};
`;

const BookingSelect = styled.select`
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.colors.gray.light};
  background: ${(p) => p.theme.colors.white};
  font-size: 16px;
`;

const Bulle = styled(Box)`
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${(p) =>
    p.$mine ? p.theme.colors.blue.pale : p.theme.colors.gray.lightest};
`;

/**
 * FIXERLIGHT-049 — détail d'un fil de messages, avec réponse (contrat § 2).
 * Même famille et mêmes patrons que `WorkshopBookingDetail`
 * (FIXERLIGHT-052) : atelier, chargement, erreur, garde de connexion.
 *
 * Route `/messages/:id`, à la RACINE du site (imposé par api#{lien mail de
 * réponse, `MessageReplyReceivedMail::link()`, contrat § 6) — pas
 * `/account/...`. Un visiteur non connecté qui suit ce lien passe par
 * `/login?redir=...` puis revient exactement ici.
 */
function MessageDetail() {
  const { t } = useTranslation();
  const client = useSelector((s) => s.client);
  const history = useHistory();
  const { id: threadId } = useParams();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  // FIXERLIGHT-300 : '' = « aucun rendez-vous » (valeur initiale), sinon
  // l'id du RDV choisi dans `thread.bookings`.
  const [bookingChoice, setBookingChoice] = useState('');

  useEffect(() => {
    if (!client.id) {
      history.push(
        `/login?redir=${encodeURIComponent(`/messages/${threadId}`)}`,
      );
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await getMessageThread(client.id, threadId);
        if (cancelled) return;
        setThread(data);
      } catch (e) {
        if (cancelled) return;
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, threadId]);

  const bookings = (thread && thread.bookings) || [];
  const hasBookings = bookings.length > 0;

  const submitReply = async () => {
    if (!body.trim()) return;
    setSending(true);
    setSendError(null);
    const bookingId = bookingChoice === '' ? null : Number(bookingChoice);
    try {
      let updated;
      if (thread.status === 'closed' && bookingId !== null) {
        // Un RDV choisi sur un fil clos : la route RDV rouvre le même fil
        // (même atelier, même cycliste) ; `POST .../message-threads`
        // ignorerait booking_id.
        updated = await sendWorkshopBookingMessage(client.id, bookingId, body);
      } else if (thread.status === 'closed') {
        // Un fil clos ne se rouvre jamais par `/reply` (contrat § 2) :
        // seul `POST .../message-threads`, qui vise l'atelier et pas un
        // fil précis, le fait — il lui faut le `shop_id` numérique que
        // `ClientMessageThreadResource` n'expose pas (juste `shop.url`).
        const shopId = await resolveShopIdFromUrl(
          thread.shop && thread.shop.url,
        );
        if (!shopId) {
          setSendError('reopen_unavailable');
          return;
        }
        updated = await openMessageThread(client.id, {
          shopId,
          body,
          origin: 'directory',
        });
      } else {
        updated = await replyMessageThread(
          client.id,
          threadId,
          body,
          hasBookings ? bookingId : undefined,
        );
      }
      setThread(updated);
      setBody('');
      setBookingChoice('');
    } catch (e) {
      setSendError((e && e.kind) || 'generic');
    } finally {
      setSending(false);
    }
  };

  const statusKey = thread && messageThreadStatusKey(thread.status);

  return (
    <Container>
      <Container fluid pb={10} pt={4}>
        <Helmet
          title={t('messages.detail.meta.title')}
          meta={[{ name: 'robots', content: 'noindex, nofollow' }]}
        />
        <Row md gap={56}>
          <AccountNav />
          <Box mt={{ xs: -7, lg: 0 }} style={{ flex: 1 }}>
            <Link
              to="/messages"
              color="black"
              mb={4}
              data-testid="message-thread-detail-back">
              &larr; {t('messages.detail.back')}
            </Link>

            {loading && (
              <Box mt={4} data-testid="message-thread-detail-skeleton">
                <Skeleton style={{ borderRadius: 8 }} height={220} />
              </Box>
            )}

            {!loading && error && (
              <ErrorBox data-testid="message-thread-detail-error">
                {t('messages.detail.error')}
              </ErrorBox>
            )}

            {!loading && !error && thread && (
              <Box mt={4}>
                <Box
                  flexDirection="row"
                  flexWrap="wrap"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ rowGap: 8 }}
                  mb={4}>
                  <Heading level={2} data-testid="message-thread-shop">
                    {thread.shop && thread.shop.name}
                  </Heading>
                  {statusKey && (
                    <Badge
                      color={messageThreadStatusColor(thread.status)}
                      light
                      data-testid="message-thread-status">
                      {t(statusKey)}
                    </Badge>
                  )}
                </Box>

                <StyledBox
                  borderRadius="xs"
                  p={4}
                  mb={3}
                  data-testid="message-thread-messages">
                  {(thread.messages || []).map((message, index) => (
                    <Box
                      key={index}
                      alignItems={
                        message.sender === 'client' ? 'flex-end' : 'flex-start'
                      }
                      mb={3}
                      data-testid="message-thread-message">
                      <Bulle $mine={message.sender === 'client'}>
                        <Text>{message.body}</Text>
                      </Bulle>
                      <Text fontSize="xs" color="gray.dark" mt={1}>
                        {t(
                          message.sender === 'client'
                            ? 'messages.detail.from.client'
                            : 'messages.detail.from.fixer',
                        )}{' '}
                        · {majuscule(horaireLong(message.sent_at))}
                      </Text>
                    </Box>
                  ))}
                </StyledBox>

                {thread.status === 'closed' && (
                  <Text
                    color="gray.dark"
                    mb={2}
                    data-testid="message-thread-closed-note">
                    {t('messages.detail.closedNote')}
                  </Text>
                )}

                {hasBookings && (
                  <Box mb={2}>
                    <Text
                      as="label"
                      htmlFor="message-thread-booking"
                      fontSize={1}
                      color="gray.dark"
                      mb={1}>
                      {t('messages.detail.booking.label')}
                    </Text>
                    <BookingSelect
                      id="message-thread-booking"
                      value={bookingChoice}
                      onChange={(e) => setBookingChoice(e.target.value)}
                      disabled={sending}
                      data-testid="message-thread-booking-select">
                      <option value="">
                        {t('messages.detail.booking.none')}
                      </option>
                      {bookings.map((booking) => (
                        <option key={booking.id} value={booking.id}>
                          {[
                            booking.scheduled_at &&
                              majuscule(horaireLong(booking.scheduled_at)),
                            booking.reference,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </option>
                      ))}
                    </BookingSelect>
                  </Box>
                )}

                <Input
                  as="textarea"
                  rows={3}
                  name="message-thread-reply"
                  bg="white"
                  placeholder={t('messages.detail.reply.placeholder')}
                  value={body}
                  onChange={(e) =>
                    setBody(e.target.value.slice(0, MAX_MESSAGE_LENGTH))
                  }
                  maxLength={MAX_MESSAGE_LENGTH}
                  data-testid="message-thread-reply-input"
                />
                <Text
                  fontSize={0}
                  color="gray.dark"
                  textAlign="right"
                  mt={1}
                  data-testid="message-thread-reply-counter">
                  {body.length}/{MAX_MESSAGE_LENGTH}
                </Text>

                {sendError && (
                  <ErrorBox data-testid="message-thread-reply-error">
                    {t(messageThreadActionErrorKey(sendError))}
                  </ErrorBox>
                )}

                <Box mt={2} alignItems="flex-start">
                  <Button
                    disabled={!body.trim() || sending}
                    onClick={submitReply}
                    data-testid="message-thread-reply-submit">
                    {t('messages.detail.reply.submit')}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Row>
      </Container>
    </Container>
  );
}

export default MessageDetail;
